import * as clone from 'clone';
import { VERSION } from 'environments/version';
import * as moment from 'moment';

/**
 * Shared utilities
 */


function capitalizeSentence(sentence: string) {
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export default {
    /**
     * This functions checks if the 'cellDateString' is in the
     * date range.
     *  filterDateFrom < cellDateString < filterDateTo
     *
     *  Example #1:
     *      filterDateFrom:     Tue Apr 23 2024 00:00:00 GMT+0200 (Central European Summer Time)
     *      cellDateString:     2024-04-24 21:24:07+02:00
     *      filterDateTo:       Thu Apr 25 2024 00:00:00 GMT+0200 (Central European Summer Time)
     *      result:             true
     *
     *  Example #2:
     *      filterDateFrom:     Tue Apr 23 2024 00:00:00 GMT+0200 (Central European Summer Time)
     *      cellDateString:     2024-04-26 23:41:03+02:00
     *      filterDateTo:       Thu Apr 25 2024 00:00:00 GMT+0200 (Central European Summer Time)
     *      result:             false
     *
     * Returns:
     *  Boolean
     *      - True: cell date in the specified date range
     *      - False: cell date not in the specified date range
     * @param filterDateFrom (Date)
     * @param filterDateTo (Date)
     * @param cellDateString (string)
     */
    dateFilterComparator([filterDateFrom, filterDateTo], cellDateString) {
        const cellDate = new Date(cellDateString);

        // Add one day to the end date
        filterDateTo.setDate(filterDateTo.getDate() + 1);

        return (filterDateFrom < cellDate && filterDateTo > cellDate);
    },

    isObjectEmpty(obj: {}) {
        return !obj || Object.keys(obj).length === 0;
    },

    SENTINEL: null,
    UPDATE_INTERVAL_MS: 3000,
    COMMAND_IN_PROGRESS_DELAY_SECONDS: 22,
    // Contain at least one dot in the middle but none at beginning or end
    SERVER_FQDN_PATTERN: /^(?!.*\.$)(?!^\.).*\..*$/,
    SHARED_DOWNLOAD_LOCATION_PATTERN: /^(?:\\{0,2}|\/{0,2})(?:$|(?:(?!(?:CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:[\\\/]|$))[^\x00-\x1F<>:"/\\|?*]{1,255}(?<![.])[\\\/])*(?!(?:CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:[\\\/]|$))[^\x00-\x1F<>:"/\\|?*]{1,255}(?<![.])[\\\/]?)$/u,
    SHARED_DOWNLOAD_USERNAME_PATTERN: /^(?:[^@\\\/]+|[^@\\/\s]+@[^@\\/\s]+|[^\\/\s]+\\[^\\/\s]+)$/,
    MAX_SHARED_DOWNLOAD_LOCATION_LENGTH: 256,
    // Limit for fetching the AC Logs (lambda limitation)
    AC_LOG_LIMIT: 300,

    sleep(duration: number = 1000) {
        return new Promise(r => setTimeout(r, duration));
    },

    deepCopy(value) {
        return clone(value);
    },

    parserServerError(error: string): string {
        switch (error) {
            case 'internal_server_error': {
                return 'An error occurred in the server, please try again' +
                    ' later';
            }
            case 'ac_name_must_be_unique_per_customer': {
                return 'CID name is already in use';
            }
            case 'has_acs': {
                return 'There are CIDs associated with the server,' +
                    ' please remove them to continue';
            }
            case 'ac_fields_not_changed': {
                return 'CID fields will not be updated because no' +
                    ' changes were detected';
            }
            case 'ac_nic_fields_not_changed': {
                return 'Network settings will not be updated because no' +
                    ' changes were detected';
            }
            case 'customer_fields_not_changed': {
                return 'Account fields will not be updated because no' +
                    ' changes were detected';
            }
            case 'set_ac_hostname_command_desired_or_executing': {
                return 'Cannot process changes to CID details because ' +
                    'previous changes are still pending';
            }
            case 'windows_update_duplicated': {
                return 'The file contains duplicated Windows update entries';
            }
            default: {
                return error;
            }

        }
    },

    getIconFileUrl(iconName) {
        const commitHash = VERSION.split(' ')[1] || '';
        return `/assets/icons/${iconName}.svg?${commitHash}`;
    },

    getSanitizedDnsAddress(dns) {
        if (!dns || dns.indexOf(':') > -1) {
            return '';
        }
        return dns;
    },

    getSecondsAgo(datetimeString) {
        return moment.utc().diff(
            moment.parseZone(datetimeString), 'seconds');
    },

    // This method returns true if the shadow has a command in pending or started status,
    // and a command that is included in commandNames. This function also returns true
    // if the shadow has a command that matches commandNames and finished or failed less than
    // COMMAND_IN_PROGRESS_DELAY_SECONDS seconds ago.
    isCommandInProgress(shadowLog: any, commandNames) {
        const reportedState = shadowLog['log_details']['shadow_json']['state']['reported'];
        const reportedCmd = reportedState && reportedState['command'];
        const commandName = reportedCmd && reportedCmd['name'];
        const status = reportedCmd && reportedCmd['execution_status'];

        if (!commandNames.includes(commandName)) {
            return false;
        }
        const isCommandInProgress = ['pending', 'started'].includes(status);
        if (isCommandInProgress) {
            return true;
        }
        const isCommandFinished = ['finished', 'failed'].includes(status);
        const secondsAgo = this.getSecondsAgo(shadowLog.created_date);
        if (isCommandFinished && secondsAgo <= this.COMMAND_IN_PROGRESS_DELAY_SECONDS) {
            return true;
        }
        return false;
    },

    async getSvgByUrl(url) {
        const commitHash = VERSION.split(' ')[1] || '';

        const response = await fetch(`${url}?${commitHash}`);
        const svg = await response.text();
        return svg;
    },

    trimTextAndAddEllipses(text, maxLength) {
        if (text.length > maxLength) {
            const trimmedTextWithEllipses = text.substring(0, maxLength) + '...';
            return trimmedTextWithEllipses;
        }
        else {
            return text;
        }
    },

    isOnlySpaces(word: string) {
        if (word === '') {
            return false;
        }
        return word.trim().length === 0;
    },

    capitalizeSentence,

    getChangesMessage(data, fields) {
        const changeMessages = [];
        for (const field of fields) {
            const change = data[field.key];
            if (change && change.orig !== change.new) {
                changeMessages.push(
                    `changed ${field.label} to '${change.new}' from '${change.orig}'`);
            }
        }
        return capitalizeSentence(changeMessages.join('; '));
    },

    htmlEncode(text) {
        const el = document.createElement('div');
        el.innerText = el.textContent = text;
        return el.innerHTML;
    },

    /**
     * This function removes all keys in localStorage except those keys
     * that we want to keep.
     */
    clearLocalStorage() {
        const keys = Object.keys(localStorage);
        const keysToKeep = new Set(['gridColumnWidthsJson']);
        for (const key of keys) {
            if (keysToKeep.has(key)) {
                continue;
            }
            localStorage.removeItem(key);
        }
    },

    /**
     * Recursively remove whitespace from all object values.
     * @param objectData The object who values need to be trimmed
     * @param excludedKeys a list of keys whose values should not be trimmed e.g. ['password']
     */
    trimObjectValues(objectData, excludedKeys?: any []) {
        if (!objectData) {
            return;
        }
        Object.keys(objectData).forEach((key: any) => {
            const isExcluded = !excludedKeys ?
                false : excludedKeys.findIndex(exKey => exKey == key) !== -1;

            if (isExcluded) {
                return;
            }
            const value = objectData[key];
            if (typeof value === 'object') {
                this.trimObjectValues(value, excludedKeys);
            }
            else if (typeof value === 'string') {
                objectData[key] = value.trim();
            }
        });
    },

    /**
     * Creates nested empty objects inside obj using the keys array. If
     * a key already exists, it is not overwritten.
     *
     * Example:
     *      obj:    {}
     *      keys:   [ 'a', 'b', 'c' ]
     *   => obj:    { a: { b: { c: {} } } }
     */
    createNestedEmptyObjects(obj, keys) {
        let nextObj = obj;
        for (const key of keys) {
            if (!nextObj[key]) {
                nextObj[key] = {};
            }
            nextObj = nextObj[key];
        }
    },

    /**
     * This functions opens the content on another page and opens the 'print' dialog.
     *
     * Beware: this function can fail and return an error due to failure in opening a new window tab,
     *  that can happen due to browser privacy settings, Ad blocker.
     *  The issue is not common.
     *
     * @param title title of the opened page
     * @param content content of the page (HTML, ..)
     */
    printContent(title: string = 'Print Content', content: string = '') {
        const newContentPage = window.open('about:blank', '_new');

        // Check if a window is opened successfully
        if (!newContentPage) {
            // Window failed to open, most likely due to browser privacy settings, Ad blocker
            console.error('Failed to open the new browser tab ' +
                '(popup was blocked - browser privacy settings, Ad blocker). Please try again!');
            throw new Error('Failed to open the new browser page. Please try again!');
        }

        newContentPage.document.title = title;
        newContentPage.document.open();
        newContentPage.document.write(content);
        newContentPage.document.close();
        newContentPage.print();
    },

    /**
     * This function dynamically accesses nested properties of an object,
     *  returning the value/undefined.
     *
     * If key does not exist 'undefined' is returned.
     *
     * @param obj
     * @param key key inside the object
     *  example: instruments.1.name
     */
    getValueByKey(obj, key) {
        return key.split('.').reduce((accumulatedItem, k) => accumulatedItem?.[k], obj);
    },

    getFileContentAsText(file: File) {
        const fileReader = new FileReader();

        return new Promise((resolve, reject) => {
           fileReader.onload = (event) => {
             const fileContent = fileReader.result;
             resolve(fileContent);
           };

           fileReader.onerror = (error) => {
               reject(error);
           }

           fileReader.readAsText(file);
        });
    },

    getDictById(items) {
        const itemsById = {};
        for (const item of items) {
          itemsById[item.id] = item;
        }
        return itemsById;
    }
};
